// Backups: the one protection that survives losing every device at once (no server keeps a copy).
// Built straight from the local store, so it also works for leftover data of an old space.

import type { Store } from '../space/store';
import { NoteDoc } from './doc';
import type { Folder, NoteMeta } from './notes';
import { writeNotdZip, type ExportNote } from './notd';

const LAST = 'lastBackup';
export const BACKUP_EVERY_MS = 7 * 86_400_000;

/** Every note of the store (archived included) as a notd-format zip. */
export async function exportStore(store: Store): Promise<{ zip: Uint8Array; count: number }> {
  const folders = new Map(((await store.getKv<Folder[]>('folders')) ?? []).map((f) => [f.id, f.name]));
  const items: ExportNote[] = [];
  for (const meta of await store.allIndex<NoteMeta>()) {
    const state = await store.getDoc(meta.id);
    const doc = state ? new NoteDoc(state) : NoteDoc.fromUpdates((await store.updatesFor(meta.id)).map((u) => u.update));
    const fields = doc.fields();
    items.push({ fields, body: doc.text(), folderName: fields.folder ? folders.get(fields.folder) ?? null : null, edited: meta.edited });
  }
  return { zip: writeNotdZip(items), count: items.length };
}

export async function lastBackup(store: Store): Promise<number | null> {
  return (await store.getKv<number>(LAST)) ?? null;
}

/**
 * Builds the backup and hands it to the user: the share sheet on phones (Files, iCloud Drive…),
 * a download elsewhere — or when sharing is refused (iOS drops the user gesture after async work).
 */
export async function saveBackup(store: Store, save: (f: File) => void, share: (f: File[]) => Promise<void>, canShare: (f: File[]) => boolean): Promise<number> {
  const { zip, count } = await exportStore(store);
  const file = new File([zip as Uint8Array<ArrayBuffer>], `notes-${new Date().toISOString().slice(0, 10)}.zip`, { type: 'application/zip' });
  const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  let shared = false;
  if (coarse && canShare([file])) {
    try {
      await share([file]);
      shared = true;
    } catch {
      shared = false;
    }
  }
  if (!shared) save(file);
  await store.setKv(LAST, Date.now());
  return count;
}
